"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Group = void 0;
const compute_manager_1 = require("../../compute_manager");
const store_1 = require("../../exports/store");
const selectors_1 = require("../../exports/store/selectors");
class Group {
    constructor(groupInfo, groupBreakpoint) {
        this.groupArray = [];
        this.groupTabIdMap = new Map();
        this.groupArray = groupInfo.map(f => f.fieldId);
        this.groupBreakpoint = this.groupArray.length && groupBreakpoint ? groupBreakpoint : {};
        if (!groupBreakpoint) {
            groupInfo.forEach(groupItem => {
                this.groupBreakpoint[groupItem.fieldId] = [];
            });
        }
    }
    clearGroupBreakpoint() {
        compute_manager_1.computeCache.delete('groupBreakpoint');
    }
    cacheGroupBreakpoint() {
        compute_manager_1.computeCache.set('groupBreakpoint', this.groupBreakpoint);
    }
    addBreakpointAndSetGroupTab(groupFieldId, breakIndex, fieldId, depth) {
        if (this.groupBreakpoint[groupFieldId].includes(breakIndex))
            return;
        this.groupBreakpoint[groupFieldId].push(breakIndex);
        if (fieldId) {
            this.groupTabIdMap.set(`${fieldId}_${depth}`, true);
        }
    }
    getAllGroupTabIdsByRecomputed(state) {
        var _a;
        const map = new Map();
        if (!((_a = this.groupArray) === null || _a === void 0 ? void 0 : _a.length))
            return map;
        const rows = (0, selectors_1.getVisibleRows)(state);
        this.groupArray.forEach((groupFieldId, depth) => {
            (this.groupBreakpoint[groupFieldId] || []).forEach((recordIndex) => {
                const row = rows[recordIndex];
                if (row) {
                    const recordId = row.recordId;
                    map.set(`${recordId}_${depth}`, true);
                }
            });
        });
        return map;
    }
    getAllGroupTabIds() {
        return this.groupTabIdMap;
    }
    /**
     * Get all breakpoints under the current breakpoint (ie, subgroup headers under the current group)
     * Level 1 group breakpoint 0---------10---------20
     * Level 2 group breakpoint 0--3-5-6--10----15---20
     * 0 => [3,5,6]
     */
    getChildBreakpointIds(state, recordId, depth) {
        const rowIndexMap = (0, selectors_1.getVisibleRowsIndexMap)(state);
        const rows = (0, selectors_1.getVisibleRows)(state);
        const startVisibleRowIndex = rowIndexMap.get(recordId);
        const breakPoints = this.groupBreakpoint[this.groupArray[depth]];
        if (!breakPoints)
            return [];
        const breakPointIndex = breakPoints.findIndex(i => i === startVisibleRowIndex);
        const endVisibleRowIndex = breakPoints[breakPointIndex + 1];
        const childGroupFieldId = this.groupArray[depth + 1];
        if (childGroupFieldId) {
            const childBreakpoints = this.groupBreakpoint[childGroupFieldId];
            if (childBreakpoints) {
                const startIndex = childBreakpoints.findIndex(i => i === startVisibleRowIndex);
                const endIndex = childBreakpoints.findIndex(i => i === endVisibleRowIndex);
                return childBreakpoints.slice(startIndex, endIndex).map(recordIndex => { var _a; return (_a = rows[recordIndex]) === null || _a === void 0 ? void 0 : _a.recordId; });
            }
        }
        return [];
    }
    // Get all rows under the grouping hierarchy.
    getRecordsInGroupByDepth(state, recordId, depth) {
        const rowIndexMap = (0, selectors_1.getVisibleRowsIndexMap)(state);
        const visibleRows = (0, selectors_1.getVisibleRows)(state);
        const startVisibleRowIndex = rowIndexMap.get(recordId);
        const breakPointArray = this.groupBreakpoint[this.groupArray[depth]];
        if (!breakPointArray)
            return [];
        const breakPointIndex = breakPointArray.findIndex(i => i === startVisibleRowIndex);
        const endVisibleRowIndex = breakPointArray[breakPointIndex + 1];
        return visibleRows.slice(startVisibleRowIndex, endVisibleRowIndex);
    }
    /**
     * According to the breakpoint, generate the Row of the grouping structure between the breakpoint Records
     * @param breakIndex
     */
    genGroupLinearRows(breakIndex, recordId, preRecordId) {
        var _a;
        const res = [];
        let breakPointGroupLevel = 0;
        for (const [index, fid] of this.groupArray.entries()) {
            if ((_a = this.groupBreakpoint[fid]) === null || _a === void 0 ? void 0 : _a.includes(breakIndex)) {
                breakPointGroupLevel = index;
                break;
            }
        }
        // Add row at the end of the previous group.
        if (preRecordId) {
            const depth = this.groupArray.length;
            res.push({
                type: store_1.CellType.Add, depth,
                recordId: preRecordId,
            });
        }
        const addBlankLength = this.groupArray.length - breakPointGroupLevel;
        // Empty rows from the last grouping.
        for (const i of [...Array(addBlankLength).keys()]) {
            if (i == addBlankLength - 1) {
                if (recordId) {
                    const depth = this.groupArray.length - 1 - i;
                    res.push({
                        type: store_1.CellType.Blank, depth,
                        recordId: recordId,
                    });
                }
            }
            else if (preRecordId) {
                const depth = this.groupArray.length - 1 - i;
                res.push({
                    type: store_1.CellType.Blank, depth,
                    recordId: preRecordId,
                });
            }
        }
        // tab of the current group
        for (const i of [...Array(this.groupArray.length - breakPointGroupLevel).keys()].reverse()) {
            if (recordId) {
                const depth = this.groupArray.length - 1 - i;
                res.push({
                    type: store_1.CellType.GroupTab,
                    depth,
                    recordId,
                });
            }
        }
        return res;
    }
    /**
     * Get the breakpoint of the deepest grouping
     */
    getDepthGroupBreakPoints() {
        const groupLength = this.groupArray.length;
        const depthGroupId = this.groupArray[groupLength - 1];
        return this.groupBreakpoint[depthGroupId];
    }
}
exports.Group = Group;
//# sourceMappingURL=group.js.map